import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Star, ChevronDown, ChevronUp, ChevronRight, RotateCcw, Save } from "lucide-react";
import { LEGE_VOORKEUREN, subKey, itemKey, toggleInLijst, verplaats } from "@/lib/menuVoorkeuren";

export function MenuInstellingenDialoog({ open, onClose, groups, voorkeuren, onSave, onReset }) {
  const [vk, setVk] = useState({ ...LEGE_VOORKEUREN, ...voorkeuren });
  const [openCat, setOpenCat] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setVk({ ...LEGE_VOORKEUREN, ...voorkeuren }); }, [open, voorkeuren]);

  const volgorde = useMemo(() => {
    const bekend = vk.volgorde_categorieen.filter((c) => groups.some((g) => g.cat === c));
    return [...bekend, ...groups.map((g) => g.cat).filter((c) => !bekend.includes(c))];
  }, [vk.volgorde_categorieen, groups]);
  const gesorteerd = volgorde.map((c) => groups.find((g) => g.cat === c)).filter(Boolean);
  const aantalVerborgen = vk.verborgen_categorieen.length + vk.verborgen_subgroepen.length + vk.verborgen_items.length;

  const upd = (veld, key) => setVk((s) => ({ ...s, [veld]: toggleInLijst(s[veld], key) }));
  const schuif = (cat, r) => setVk((s) => ({ ...s, volgorde_categorieen: verplaats(volgorde, cat, r) }));
  const opslaan = async () => { setBusy(true); try { await onSave(vk); onClose(); } finally { setBusy(false); } };
  const herstel = async () => { setBusy(true); try { await onReset(); setVk({ ...LEGE_VOORKEUREN }); onClose(); } finally { setBusy(false); } };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="menu-instellingen-dialoog">
        <DialogHeader>
          <DialogTitle>Menu aanpassen</DialogTitle>
          <DialogDescription className="text-xs">
            Alleen voor jou — je rechten veranderen niet. Wat je verbergt verdwijnt uit het menu, blijft vindbaar via zoeken en staat onderaan
            bij <b>Verborgen onderdelen</b>. Met de ster zet je favorieten bovenaan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1" data-testid="menu-instellingen-lijst">
          {gesorteerd.map((g, i) => {
            const catHidden = vk.verborgen_categorieen.includes(g.cat);
            const isOpen = openCat === g.cat;
            return (
              <div key={g.cat} className={`rounded-md border ${catHidden ? "border-dashed border-slate-200 bg-slate-50/60" : "border-slate-200 bg-white"}`} data-testid={`menu-cat-${g.cat}`}>
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <button onClick={() => setOpenCat(isOpen ? null : g.cat)} className="p-1 text-slate-500 hover:text-slate-800" aria-label="Uitklappen" data-testid={`menu-cat-toggle-${g.cat}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <span className={`flex-1 text-xs font-bold uppercase tracking-wider ${catHidden ? "text-slate-400 line-through" : "text-slate-800"}`}>{g.cat}</span>
                  <span className="text-[10px] text-slate-400 mr-1">{g.items.length}</span>
                  <button onClick={() => schuif(g.cat, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" aria-label="Omhoog" data-testid={`menu-cat-up-${g.cat}`}><ChevronUp className="h-4 w-4" /></button>
                  <button onClick={() => schuif(g.cat, 1)} disabled={i === gesorteerd.length - 1} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" aria-label="Omlaag" data-testid={`menu-cat-down-${g.cat}`}><ChevronDown className="h-4 w-4" /></button>
                  <OogKnop verborgen={catHidden} onClick={() => upd("verborgen_categorieen", g.cat)} testId={`menu-cat-oog-${g.cat}`} />
                </div>
                {isOpen && <CategorieInhoud g={g} vk={vk} upd={upd} />}
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border">
          <span className="text-[11px] text-slate-500" data-testid="menu-instellingen-samenvatting">{aantalVerborgen} verborgen · {vk.favorieten.length} favoriet(en)</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={herstel} disabled={busy} data-testid="menu-instellingen-herstel" data-rechten="lezen"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Standaard</Button>
            <Button size="sm" onClick={opslaan} disabled={busy} data-testid="menu-instellingen-opslaan" data-rechten="lezen"><Save className="h-3.5 w-3.5 mr-1" /> Opslaan</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategorieInhoud({ g, vk, upd }) {
  const rijen = [];
  let lastSub = null;
  g.items.forEach((it, idx) => {
    if (it.subgroup && it.subgroup !== lastSub) {
      const k = subKey(g.cat, it.subgroup);
      const subHidden = vk.verborgen_subgroepen.includes(k);
      rijen.push(
        <div key={`sub-${idx}`} className="flex items-center gap-1 pl-7 pr-2 pt-1.5" data-testid={`menu-sub-${g.cat}-${it.subgroup}`}>
          <span className={`flex-1 text-[10px] uppercase tracking-wider font-semibold ${subHidden ? "text-slate-300 line-through" : "text-slate-500"}`}>{it.subgroup}</span>
          <OogKnop verborgen={subHidden} onClick={() => upd("verborgen_subgroepen", k)} testId={`menu-sub-oog-${g.cat}-${it.subgroup}`} klein />
        </div>
      );
      lastSub = it.subgroup;
    } else if (!it.subgroup) lastSub = null;
    const key = itemKey(it);
    const hidden = vk.verborgen_items.includes(key);
    const fav = vk.favorieten.includes(key);
    const Icon = it.icon;
    rijen.push(
      <div key={key + idx} className="flex items-center gap-2 pl-9 pr-2 py-1" data-testid={`menu-item-${it.tid}`}>
        <Icon className={`h-3.5 w-3.5 ${hidden ? "text-slate-300" : "text-slate-500"}`} />
        <span className={`flex-1 text-sm truncate ${hidden ? "text-slate-400 line-through" : "text-slate-700"}`}>{it.label}</span>
        <button onClick={() => upd("favorieten", key)} className={`p-1 ${fav ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`} aria-label="Favoriet" data-testid={`menu-item-ster-${it.tid}`}>
          <Star className="h-3.5 w-3.5" fill={fav ? "currentColor" : "none"} />
        </button>
        <OogKnop verborgen={hidden} onClick={() => upd("verborgen_items", key)} testId={`menu-item-oog-${it.tid}`} klein />
      </div>
    );
  });
  return <div className="pb-1.5 border-t border-slate-100">{rijen}</div>;
}

function OogKnop({ verborgen, onClick, testId, klein }) {
  const cls = klein ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <button onClick={onClick} className={`p-1 ${verborgen ? "text-rose-500 hover:text-rose-700" : "text-slate-400 hover:text-slate-800"}`}
      aria-label={verborgen ? "Tonen" : "Verbergen"} title={verborgen ? "Weer tonen" : "Verbergen"} data-testid={testId}>
      {verborgen ? <EyeOff className={cls} /> : <Eye className={cls} />}
    </button>
  );
}

export default MenuInstellingenDialoog;
